const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-dark-200 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-dark-100 rounded-lg shadow-xl p-8">
          <h1 className="text-4xl font-bold text-white mb-6">Privacy Policy</h1>
          <p className="text-gray-400 mb-8">Last Updated: October 24, 2025</p>

          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
              <p className="mb-4">
                S.C. STUDIO PLATFORMS S.R.L. ("we," "our," or "us"), a company registered in Romania with its registered office in Craiova, Romania, operates the Studio Blockchain Explorer (the "Service"). We are committed to protecting your personal data and respecting your privacy in accordance with the General Data Protection Regulation (GDPR) (EU) 2016/679 and Romanian Law 190/2018.
              </p>
              <p className="mb-4">
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our blockchain explorer service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Data Controller</h2>
              <div className="bg-dark-200 p-4 rounded-lg">
                <p className="mb-2"><strong className="text-white">Company Name:</strong> S.C. STUDIO PLATFORMS S.R.L.</p>
                <p className="mb-2"><strong className="text-white">Registered Office:</strong> Craiova, Romania</p>
                <p className="mb-2"><strong className="text-white">Email:</strong> office@studio-blockchain.com</p>
                <p className="mb-2"><strong className="text-white">Website:</strong> https://studioplatforms.eu</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3">3.1 Information You Provide</h3>
              <ul className="list-disc list-inside mb-4 space-y-2">
                <li>Contact information when you reach out to us (email address, name)</li>
                <li>Any information you voluntarily provide through forms or communications</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">3.2 Automatically Collected Information</h3>
              <ul className="list-disc list-inside mb-4 space-y-2">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
                <li>Search queries performed on our Service</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">3.3 Blockchain Data</h3>
              <p className="mb-4">
                Our Service displays publicly available blockchain data. This data is not collected by us but is retrieved from the Studio Blockchain network. Blockchain addresses and transaction data are public by nature and are not considered personal data unless they can be linked to an identifiable individual.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Legal Basis for Processing</h2>
              <p className="mb-4">We process your personal data based on the following legal grounds:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong className="text-white">Legitimate Interest:</strong> To operate and improve our Service (Article 6(1)(f) GDPR)</li>
                <li><strong className="text-white">Consent:</strong> When you provide explicit consent for specific processing activities (Article 6(1)(a) GDPR)</li>
                <li><strong className="text-white">Legal Obligation:</strong> To comply with applicable laws and regulations (Article 6(1)(c) GDPR)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. How We Use Your Information</h2>
              <p className="mb-4">We use the collected information for the following purposes:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>To provide and maintain our Service</li>
                <li>To improve user experience and Service functionality</li>
                <li>To analyze usage patterns and optimize performance</li>
                <li>To respond to your inquiries and provide customer support</li>
                <li>To detect, prevent, and address technical issues and security threats</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Data Sharing and Disclosure</h2>
              <p className="mb-4">We do not sell your personal data. We may share your information in the following circumstances:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong className="text-white">Service Providers:</strong> With third-party service providers who assist in operating our Service (e.g., hosting providers, analytics services)</li>
                <li><strong className="text-white">Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. International Data Transfers</h2>
              <p className="mb-4">
                Your data may be transferred to and processed in countries outside the European Economic Area (EEA). When we transfer data internationally, we ensure appropriate safeguards are in place, such as:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Standard Contractual Clauses approved by the European Commission</li>
                <li>Adequacy decisions by the European Commission</li>
                <li>Other legally approved transfer mechanisms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Data Retention</h2>
              <p className="mb-4">
                We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Typically:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Usage data and analytics: 24 months</li>
                <li>Contact information: Until you request deletion or 3 years of inactivity</li>
                <li>Legal compliance data: As required by applicable laws</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Your Rights Under GDPR</h2>
              <p className="mb-4">As a data subject in the EU, you have the following rights:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong className="text-white">Right of Access:</strong> Request access to your personal data</li>
                <li><strong className="text-white">Right to Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong className="text-white">Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
                <li><strong className="text-white">Right to Restriction:</strong> Request restriction of processing</li>
                <li><strong className="text-white">Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong className="text-white">Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong className="text-white">Right to Withdraw Consent:</strong> Withdraw consent at any time (where processing is based on consent)</li>
                <li><strong className="text-white">Right to Lodge a Complaint:</strong> File a complaint with a supervisory authority</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at office@studio-blockchain.com. We will respond to your request within one month.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Data Security</h2>
              <p className="mb-4">
                We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Employee training on data protection</li>
              </ul>
              <p className="mt-4">
                However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Cookies and Tracking Technologies</h2>
              <p className="mb-4">
                We use cookies and similar tracking technologies to enhance your experience. For detailed information about our use of cookies, please refer to our <a href="/cookie-policy" className="text-primary hover:text-primary-light">Cookie Policy</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Children's Privacy</h2>
              <p className="mb-4">
                Our Service is not intended for individuals under the age of 16. We do not knowingly collect personal data from children. If you are a parent or guardian and believe your child has provided us with personal data, please contact us, and we will delete such information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Supervisory Authority</h2>
              <p className="mb-4">
                If you have concerns about how we process your personal data, you have the right to lodge a complaint with the Romanian supervisory authority:
              </p>
              <div className="bg-dark-200 p-4 rounded-lg">
                <p className="mb-2"><strong className="text-white">Authority:</strong> Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP)</p>
                <p className="mb-2"><strong className="text-white">Address:</strong> B-dul G-ral. Gheorghe Magheru 28-30, Sector 1, București, Romania</p>
                <p className="mb-2"><strong className="text-white">Phone:</strong> +40 318 059 211</p>
                <p className="mb-2"><strong className="text-white">Email:</strong> anspdcp@dataprotection.ro</p>
                <p className="mb-2"><strong className="text-white">Website:</strong> www.dataprotection.ro</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">14. Changes to This Privacy Policy</h2>
              <p className="mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">15. Contact Us</h2>
              <p className="mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-dark-200 p-4 rounded-lg">
                <p className="mb-2"><strong className="text-white">Email:</strong> office@studio-blockchain.com</p>
                <p className="mb-2"><strong className="text-white">Company:</strong> S.C. STUDIO PLATFORMS S.R.L.</p>
                <p className="mb-2"><strong className="text-white">Address:</strong> Craiova, Romania</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
